import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Field,
  Input,
  Choice,
  Icon,
  Tag,
  useErrorDialog,
} from '@dayon/design-system';
import { api, ApiRequestError, isErrorCode } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { TwoFactorEnrolment } from './TwoFactorEnrolment.tsx';
import { AuthShell } from './AuthShell.tsx';
import './sign-in.css';
import { gamingTime } from '@dayon/contracts';

/**
 * ENROL is a fourth step, and it is not optional.
 *
 * An admin whose role mandates a second factor and who has none used to be
 * signed straight in with `mfa: false` — an account that reads as authorised on
 * the roster and is refused on every money click. That is also exactly where a
 * factor reset leaves somebody, so without this step Reset 2FA would have been
 * a bypass rather than a reset: clear the factor, sign in with the password
 * alone, and the platform would ask for nothing more.
 *
 * So the API answers TWO_FACTOR_ENROLMENT_REQUIRED instead of minting a
 * session, and this screen makes them enrol before anything exists to sign in
 * with.
 */
type Step = 'CREDENTIALS' | 'TWO_FACTOR' | 'ENROL' | 'DONE';

/** m:ss. A naive `0:${seconds}` renders a 90-second challenge as "0:90". */
function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

interface Challenge {
  challengeToken: string;
  email: string;
  expiresIn: number;
  /**
   * Whether this API offers the develop code helper. The SERVER decides — see
   * the note on the contract. The console ships as one build, so asking the
   * bundle what environment it is in would be asking the wrong process.
   */
  devTotpAvailable?: boolean;
}

/**
 * Sign in — the design's three steps: credentials, two-factor, signed in.
 *
 * Field-level problems stay inline beside their field. A failure that is not
 * about a field — a locked account, a network fault — goes to the error dialog,
 * which is the project's single error surface.
 */
export function SignIn() {
  const { refresh } = useSession();
  const { showError } = useErrorDialog();

  const [step, setStep] = useState<Step>('CREDENTIALS');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [fillingCode, setFillingCode] = useState(false);
  const [devNote, setDevNote] = useState<string | null>(null);
  /* The enrolment step's own state. The secret is held only for as long as the
     screen is open and is never written anywhere. */
  const [enrolment, setEnrolment] = useState<{ uri: string; secret: string } | null>(null);
  const [enrolStarting, setEnrolStarting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [inlineError, setInlineError] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  /* The countdown the design shows: "Code expires in 0:42". The enrolment step
     runs the same clock on a ten-minute challenge — scanning a code and naming
     an entry does not fit in ninety seconds. */
  useEffect(() => {
    if ((step !== 'TWO_FACTOR' && step !== 'ENROL') || seconds <= 0) return;
    const t = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [step, seconds]);

  useEffect(() => {
    if (step === 'TWO_FACTOR') codeRef.current?.focus();
  }, [step]);

  function handleFailure(error: unknown, fallbackTitle: string) {
    if (error instanceof ApiRequestError) {
      if (error.fields) {
        setFieldErrors(error.fields);
        return;
      }
      // A wrong password belongs beside the form, not in a modal that has to be
      // dismissed before the next attempt. Anything else is a real failure.
      //
      // Narrowed before it is BRANCHED on, so a code this platform does not
      // define cannot be compared against — a misspelling here would silently
      // put a wrong password in a modal, which is the one refusal that must
      // not need dismissing before the next attempt. The raw string still goes
      // to the dialog below, because DISPLAY should carry whatever arrived.
      const refusal = isErrorCode(error.code) ? error.code : undefined;
      if (refusal === 'CREDENTIALS_INVALID' || refusal === 'TWO_FACTOR_INVALID') {
        setInlineError(error.message);
        return;
      }
      showError({ title: fallbackTitle, message: error.message, code: error.code });
      return;
    }
    showError({
      title: fallbackTitle,
      message: 'Something went wrong. Try again shortly.',
    });
  }

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFieldErrors({});
    setInlineError(null);
    try {
      const result = await api.post<
        | {
            status: 'TWO_FACTOR_REQUIRED';
            challengeToken: string;
            email: string;
            expiresIn: number;
            devTotpAvailable?: boolean;
          }
        | {
            status: 'TWO_FACTOR_ENROLMENT_REQUIRED';
            challengeToken: string;
            email: string;
            expiresIn: number;
          }
        | { status: 'SIGNED_IN' }
      >('/api/auth/sign-in', { email, password, trustDevice });

      if (result.status === 'TWO_FACTOR_REQUIRED') {
        setChallenge(result);
        setSeconds(result.expiresIn);
        setStep('TWO_FACTOR');
      } else if (result.status === 'TWO_FACTOR_ENROLMENT_REQUIRED') {
        setChallenge(result);
        setSeconds(result.expiresIn);
        setStep('ENROL');
        setCode('');
        await startEnrolment(result.challengeToken);
      } else {
        await refresh();
        setStep('DONE');
      }
    } catch (error) {
      handleFailure(error, 'Could not sign in');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Fill the field with the code `pnpm totp` would have printed.
   *
   * Deliberately stops at filling. Submitting for you would mean the second
   * factor was never exercised by anything a person did, and this screen's job
   * in develop is partly to prove the real two-step flow still works.
   */
  async function fillDevCode() {
    if (!challenge) return;
    setFillingCode(true);
    setInlineError(null);
    setDevNote(null);
    try {
      const result = await api.post<{ code: string; email: string; secondsRemaining: number }>(
        '/api/auth/dev/two-factor-code',
        { challengeToken: challenge.challengeToken },
      );
      setCode(result.code);
      setDevNote(`Filled the code for ${result.email} · valid ${result.secondsRemaining}s`);
      codeRef.current?.focus();
    } catch (error) {
      handleFailure(error, 'Could not generate a code');
    } finally {
      setFillingCode(false);
    }
  }

  /**
   * Fetch the secret to enrol.
   *
   * Separate from the confirmation so the QR payload is fetched once and does
   * NOT rotate while somebody is halfway through typing a code off it — the
   * server reuses the pending secret rather than minting a fresh one, and a
   * screen that asked for a new one on every render would invalidate the entry
   * the person just created in their app.
   */
  async function startEnrolment(challengeToken: string) {
    setEnrolStarting(true);
    try {
      const result = await api.post<{
        email: string;
        twoFactor: { uri: string; secret: string };
        expiresIn: number;
      }>('/api/auth/enrol-2fa/start', { challengeToken });
      setEnrolment(result.twoFactor);
      setSeconds(result.expiresIn);
    } catch (error) {
      handleFailure(error, 'Could not start the enrolment');
    } finally {
      setEnrolStarting(false);
    }
  }

  async function submitEnrolment(e: React.FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setInlineError(null);
    try {
      const result = await api.post<{ recoveryCodes: string[] }>('/api/auth/enrol-2fa/confirm', {
        challengeToken: challenge.challengeToken,
        code,
      });
      await refresh();
      /* The codes replace the form rather than being toasted away: they are
         shown once, cannot be recovered, and are the only way back in if the
         phone that was just enrolled is lost too. */
      setRecoveryCodes(result.recoveryCodes);
    } catch (error) {
      handleFailure(error, 'Could not confirm the enrolment');
    } finally {
      setBusy(false);
    }
  }

  function restart() {
    setStep('CREDENTIALS');
    setCode('');
    setChallenge(null);
    setEnrolment(null);
    setRecoveryCodes(null);
    setInlineError(null);
    setDevNote(null);
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setInlineError(null);
    try {
      await api.post('/api/auth/verify-2fa', {
        challengeToken: challenge.challengeToken,
        code,
      });
      await refresh();
      setStep('DONE');
    } catch (error) {
      handleFailure(error, 'Could not verify the code');
    } finally {
      setBusy(false);
    }
  }

  const cells = Array.from({ length: 6 }, (_, i) => code[i] ?? '');

  return (
    <AuthShell>
          {step === 'CREDENTIALS' && (
            <form className="stack signin-step" onSubmit={submitCredentials} noValidate>
              <div>
                <div className="signin-portal-kicker">SECURITY CLEARANCE LEVEL 4</div>
                <h3>Sign in</h3>
                <div className="text-subtle signin-sub">
                  Admin accounts only. Player accounts use the brand apps.
                </div>
              </div>

              <Field label="Work email" error={fieldErrors['email']}>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    type="email"
                    autoComplete="username"
                    placeholder="name@dayonops.ph"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    required
                  />
                )}
              </Field>

              <Field label="Password" error={fieldErrors['password']}>
                {({ id, describedBy, invalid }) => (
                  <div className="signin-password">
                    <input
                      id={id}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="••••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-describedby={describedBy}
                      aria-invalid={invalid}
                      required
                    />
                    <button
                      type="button"
                      className="signin-eye"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                    >
                      <Icon name={showPassword ? 'eye-slash' : 'eye'} />
                    </button>
                  </div>
                )}
              </Field>

              {inlineError && (
                <div className="notice" role="alert">
                  <Icon name="warning-circle" />
                  <div>{inlineError}</div>
                </div>
              )}

              <div className="row">
                <Choice
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.currentTarget.checked)}
                  label="Trust this device 30 days"
                />
              </div>

              <Button variant="primary" block type="submit" busy={busy}>
                Continue
                <Icon name="arrow-right" />
              </Button>

              <div className="signin-policy text-subtle">
                Five failed attempts locks the account for 15 minutes.
              </div>

              {/* `docs/DESIGN_INVENTORY.md` §3.1 specifies this link on the
                  credentials step. It was absent for as long as the console
                  has existed, because there was nothing behind it: a
                  locked-out administrator's only route back was another Super
                  Admin. */}
              <div className="signin-policy text-subtle">
                <a href="/forgot-password">Forgot password?</a>
              </div>
            </form>
          )}

          {step === 'TWO_FACTOR' && (
            <form className="stack signin-step" onSubmit={submitCode} noValidate>
              <div>
                <div className="signin-portal-kicker">STEP 2 OF 2 · TIME-BASED OTP</div>
                <h3>Two-factor code</h3>
                <div className="text-subtle signin-sub">
                  Enter the 6-digit code from your authenticator app for {challenge?.email}.
                </div>
              </div>

              {/* Presentational: the real input is below and carries the label,
                  so a screen reader and a password manager both work normally. */}
              <div className="otp-row" aria-hidden="true">
                {cells.map((c, i) => (
                  <div key={`otp-${i}`} className={c ? 'otp-cell filled' : 'otp-cell'}>
                    {c || '·'}
                  </div>
                ))}
              </div>

              <Field label="Authentication code">
                {({ id }) => (
                  <Input
                    id={id}
                    ref={codeRef}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="Type the 6-digit code"
                    className="signin-code"
                    value={code}
                    onChange={(e) => {
                      const next = e.target.value.trim();
                      // A recovery code carries a dash; a TOTP code is digits.
                      setCode(next.includes('-') ? next.toUpperCase().slice(0, 11) : next.replace(/\D/g, '').slice(0, 6));
                      setInlineError(null);
                    }}
                  />
                )}
              </Field>

              {/* Shown only because the API said so — the helper is off unless
                  AUTH_DEV_TOTP_HELPER was written down. Styled as a strip rather
                  than a second primary button: it must never read as part of the
                  product a customer signs in with. */}
              {challenge?.devTotpAvailable && (
                <div className="signin-dev">
                  <div className="signin-dev-head">
                    <Tag tone="outline">DEV</Tag>
                    <span className="text-subtle">The sample admins use a secret no phone can scan.</span>
                  </div>
                  <Button
                    small
                    block
                    onClick={fillDevCode}
                    busy={fillingCode}
                    disabled={seconds <= 0}
                  >
                    <Icon name="key" />
                    Generate OTP
                  </Button>
                  {devNote && (
                    <div className="signin-dev-note text-subtle" role="status">
                      {devNote}
                    </div>
                  )}
                </div>
              )}

              {inlineError && (
                <div className="notice" role="alert">
                  <Icon name="warning-circle" />
                  <div>{inlineError}</div>
                </div>
              )}

              <Button variant="primary" block type="submit" busy={busy}>
                Verify and sign in
              </Button>

              <div className="row signin-countdown">
                <span className="text-subtle">
                  {seconds > 0
                    ? `Code request expires in ${formatCountdown(seconds)}`
                    : 'That request expired — start again.'}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost header-spacer"
                  onClick={() => {
                    setStep('CREDENTIALS');
                    setCode('');
                    setInlineError(null);
                    setDevNote(null);
                  }}
                >
                  Use another method
                </button>
              </div>

              {/* `.notice` is a flex row, so prose has to arrive as one child:
                  loose text beside a <span> becomes separate flex items and the
                  sentence breaks into columns. */}
              <div className="notice notice-quiet">
                <div>
                  Mandatory for any role holding{' '}
                  <span className="signin-perm">funds.approve</span> or{' '}
                  <span className="signin-perm">promo.publish</span>. A recovery code works here
                  too.
                </div>
              </div>
            </form>
          )}

          {step === 'ENROL' && (
            <TwoFactorEnrolment
              email={challenge?.email ?? ''}
              uri={enrolment?.uri ?? null}
              secret={enrolment?.secret ?? null}
              code={code}
              onCode={(v) => {
                setCode(v);
                setInlineError(null);
              }}
              busy={busy}
              starting={enrolStarting}
              inlineError={inlineError}
              seconds={seconds}
              onSubmit={submitEnrolment}
              onRestart={restart}
              recoveryCodes={recoveryCodes}
            />
          )}

      {step === 'DONE' && <SignedIn />}
    </AuthShell>
  );
}

/** The design's third step: a confirmation with the session's real facts. */
function SignedIn() {
  const { session } = useSession();
  if (!session) return null;

  const scope =
    session.brands.length === 0
      ? 'all brands'
      : session.brands.map((b) => b.name).join(', ');

  return (
    <div className="stack signin-step">
      <div className="signin-check">
        <Icon name="shield-check" />
      </div>
      <div>
        <h3>Signed in</h3>
        <div className="text-subtle signin-sub">
          {session.displayName} · {session.role} · scope: {scope}
        </div>
      </div>

      <div className="signin-facts">
        <div className="row">
          <span className="text-subtle">Session</span>
          <span className="header-spacer">
            expires {gamingTime(session.expiresAt)} · idle timeout{' '}
            {session.idleTimeoutMinutes}m
          </span>
        </div>
        <div className="row">
          <span className="text-subtle">Verification</span>
          <span className="header-spacer">
            {session.mfa ? 'Two-factor satisfied' : 'Password only — trusted device'}
          </span>
        </div>
      </div>

      <a className="btn btn-primary btn-block" href="/">
        Open the console
        <Icon name="arrow-right" />
      </a>
    </div>
  );
}
